import { useState } from "react";
import { Box, Button, TextField, Typography, Link } from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import "../../styles/header.css";
import BASE_URL from "../../components/config/appconfig";
import SnackbarAlert from "../../components/snackbarAlert/snackbarAlert";
import usePageMeta from "../../utils/usePageMeta";

export default function ForgotPassword() {
  usePageMeta("Forgot Password", "Reset your password for the Gospel and Church Health Framework.");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const validateEmail = (emailValue) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(emailValue);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value.trim()) {
      if (!validateEmail(value.trim())) {
        setErrors("Enter a valid email address");
      } else {
        setErrors("");
      }
    } else {
      setErrors("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrors("Email is required");
      return;
    }

    if (!validateEmail(email.trim())) {
      setErrors("Enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/admin/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: "Password reset link sent to your email",
          severity: "success",
        });
        setEmail("");
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to send reset link",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbar({
        open: true,
        message: "An error occurred. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const hideSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          px: { xs: 2, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h3"
          className="title"
          sx={{
            fontSize: {
              xs: "20px !important",
              sm: "28px !important",
              md: "32px !important",
            },
            textAlign: "center",
          }}
        >
          The Gospel and Church Health Framework
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          maxWidth: 500,
          mx: "auto",
          p: { xs: 2, sm: 4, md: 5 },
          borderRadius: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            color: "#666",
            mb: 3,
            textAlign: "center",
          }}
        >
          Enter your email address and we'll send you a link to reset your
          password.
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "12px", sm: "13px" },
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          Email Address
        </Typography>
        <TextField
          fullWidth
          required
          id="email"
          placeholder="Enter Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          type="email"
          value={email}
          onChange={handleChange}
          error={!!errors}
          helperText={errors}
          size="small"
          disabled={loading}
        />

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !!errors || !email.trim()}
            className={
              !loading && !errors && email.trim()
                ? "next-button"
                : "next-button-disabled"
            }
            sx={{
              width: { xs: "100%", sm: 120 },
              height: 48,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {loading ? "Sending..." : "Send Link"}
          </Button>
        </Box>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography fontSize={{ xs: 13, sm: 14 }}>
            <Link
              component={RouterLink}
              to="/sign-in"
              variant="body2"
              underline="hover"
              sx={{ color: "#263842", fontWeight: 600, cursor: "pointer" }}
            >
              Back to Sign In
            </Link>
          </Typography>
        </Box>
      </Box>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </>
  );
}
